import {
  CheckCircle2,
  Clock,
  Eye,
  Plus,
  RefreshCw,
  RotateCcw,
  Search,
  XCircle,
} from "lucide-react";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import Button from "@/components/ui/Button";
import SubmittedDetailsModal from "./SubmittedDetailsModal";
import { formatDate, formatINR } from "../data";
import { KYC_FEE } from "@/pages/retailer/wallet/constants";
import type { KycRecord, KycStatus } from "@/types/kyc";
import { usePlatformSettings } from "@/services/platformSettings";

interface StatusMeta {
  circle: string;
  color: string;
  icon: typeof Clock;
  title: string;
  sub: string;
}

const META: Record<KycStatus, StatusMeta> = {
  SUBMITTED: {
    circle: "bg-info-bg",
    color: "text-info",
    icon: Clock,
    title: "KYC submitted successfully",
    sub: "Your application is in the queue for review. This usually takes 24 to 48 hours.",
  },
  UNDER_REVIEW: {
    circle: "bg-warning-bg",
    color: "text-warning",
    icon: Search,
    title: "Your KYC is under review",
    sub: "Our team is verifying your documents. You will be notified once the review is complete.",
  },
  APPROVED: {
    circle: "bg-success-bg",
    color: "text-success",
    icon: CheckCircle2,
    title: "KYC approved",
    sub: "Your account is fully verified. You can now create mandates.",
  },
  REJECTED: {
    circle: "bg-danger-bg",
    color: "text-danger",
    icon: XCircle,
    title: "KYC application rejected",
    sub: "Unfortunately we could not verify your application.",
  },
  NOT_STARTED: { circle: "bg-badgeSlate-bg", color: "text-slate-600", icon: Clock, title: "", sub: "" },
  DRAFT: { circle: "bg-warning-bg", color: "text-warning", icon: Clock, title: "", sub: "" },
};

interface StatusScreenProps {
  record: KycRecord;
  kycFee?: number;
  onRefresh?: () => void;
  onResubmit?: () => void;
  refreshing?: boolean;
}

export default function StatusScreen({
  record,
  kycFee,
  onRefresh,
  onResubmit,
  refreshing,
}: StatusScreenProps) {
  const navigate = useNavigate();
  const platformSettings = usePlatformSettings();
  const [detailsOpen, setDetailsOpen] = useState(false);
  const meta = META[record.status] || META.SUBMITTED;
  const Icon = meta.icon;
  const approved = record.status === "APPROVED";
  const rejected = record.status === "REJECTED";
  const underReview = record.status === "UNDER_REVIEW" || record.status === "SUBMITTED";

  return (
    <div className="mx-auto flex w-full max-w-[640px] flex-col items-center px-4 py-8">
      <div className={`flex h-[72px] w-[72px] items-center justify-center rounded-full ${meta.circle}`}>
        <Icon className={`h-9 w-9 ${meta.color}`} />
      </div>
      <h1 className="mt-5 text-center text-2xl font-semibold text-ink">{meta.title}</h1>
      <p className="mt-2 text-center text-sm text-ink-secondary">{meta.sub}</p>

      <div className="mt-7 w-full rounded-[10px] border border-line bg-card p-5 shadow-card">
        <div className="flex items-start justify-between gap-6 border-b border-line py-2.5">
          <span className="shrink-0 text-[13px] text-ink-muted">Application ID</span>
          <span className="text-right text-[13px] font-medium text-ink">{record.applicationId || record.id || "—"}</span>
        </div>
        <div className="flex items-start justify-between gap-6 border-b border-line py-2.5">
          <span className="shrink-0 text-[13px] text-ink-muted">Shop/Business Name</span>
          <span className="text-right text-[13px] font-medium text-ink">{record.legalBusinessName || record.shopName || record.tradeName || "—"}</span>
        </div>
        <div className="flex items-start justify-between gap-6 border-b border-line py-2.5">
          <span className="shrink-0 text-[13px] text-ink-muted">Business Type</span>
          <span className="text-right text-[13px] font-medium text-ink">{record.businessType || "Proprietorship"}</span>
        </div>
        <div className="flex items-start justify-between gap-6 border-b border-line py-2.5">
          <span className="shrink-0 text-[13px] text-ink-muted">Submitted On</span>
          <span className="text-right text-[13px] font-medium text-ink">
            {formatDate(record.submittedAt || (record as any).created_at || (record as any).joinedAt || new Date().toISOString())}
          </span>
        </div>
        <div className="flex items-start justify-between gap-6 py-2.5">
          <span className="shrink-0 text-[13px] text-ink-muted">Reviewed On</span>
          <span className="text-right text-[13px] font-medium text-ink">
            {formatDate(
              record.reviewedAt ||
              record.approvedAt ||
              record.rejectedAt ||
              (approved || rejected ? (record.submittedAt || new Date().toISOString()) : null)
            )}
          </span>
        </div>
      </div>

      {rejected && (
        <div className="mt-4 w-full rounded-[10px] border border-danger/40 bg-danger-bg px-4 py-3.5">
          <p className="text-sm font-semibold text-ink">Reason for rejection</p>
          <p className="mt-1 text-[13px] text-ink-secondary">{record.rejectionReason || "Details mismatch or incomplete verification"}</p>
        </div>
      )}

      {rejected && (
        <p className="mt-4 text-center text-[13px] text-ink-secondary">
          The verification fee of {formatINR(KYC_FEE)} will be refunded to your wallet by our team.
          Please contact <a href={`mailto:${platformSettings.supportEmail}`} className="text-primary-500" rel="nofollow">{platformSettings.supportEmail}</a> for assistance.
        </p>
      )}

      <div className="mt-7 flex w-full flex-wrap items-center justify-center gap-3">
        {approved && (
          <Button onClick={() => navigate("/retailer/create-mandate")} leftIcon={<Plus className="h-4 w-4" />}>
            Create Your First Mandate
          </Button>
        )}
        {rejected && onResubmit && (
          <Button onClick={onResubmit} leftIcon={<RotateCcw className="h-4 w-4" />}>
            Edit & Resubmit KYC
          </Button>
        )}
        <Button variant="secondary" onClick={() => setDetailsOpen(true)} leftIcon={<Eye className="h-4 w-4" />}>
          View Submitted Details
        </Button>
        {onRefresh && (
          <Button
            variant="ghost"
            onClick={onRefresh}
            disabled={refreshing}
            leftIcon={<RefreshCw className={`h-4 w-4 ${refreshing ? "animate-spin" : ""}`} />}
          >
            {refreshing ? "Refreshing..." : "Check Status"}
          </Button>
        )}
      </div>

      <SubmittedDetailsModal record={record} open={detailsOpen} onClose={() => setDetailsOpen(false)} />
    </div>
  );
}